import React from "react";
import PropTypes from "prop-types";
import { Row, Box } from "jsxstyle";
import Table from "components/Table";
import Separator from "components/Separator";
import Button from "components/Button";
import DeleteCMBSTranchePrice from "./DeleteCMBSTranchePrice";
import CMBSRatingsTable from "./CMBSRatingsTable";
import PermissionValidator from "components/PermissionValidator";
import CMBSRatingsFormModal from "./CMBSRatingsFormModal";
import { ADMIN_ROLE, DATA_TEAM_ROLE } from "consts";
import orderBy from "lodash/orderBy";
import CurrencyFormat from "components/CurrencyFormat";
import PercentFormat from "components/PercentFormat";

function CMBSTranchePricesTable({
  data,
  pending,
  onReload,
  dealCode,
  onRequestEdit,
}) {
  const [editingTrancheId, setEditingTrancheId] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  return (
    <React.Fragment>
      <PermissionValidator
        allowedFor={[ADMIN_ROLE, DATA_TEAM_ROLE]}
        shouldHide={false}
      >
        {({ isAllowed }) => (
          <Table
            data={orderBy(
              data,
              ["tranche_class", (tranche) => tranche.subord_percent || ""],
              ["asc", "desc"],
            )}
            pending={pending}
            bordered={true}
            rowKey="id"
            expandedRowRender={(item) => {
              return (
                <Box maxWidth={640}>
                  <CMBSRatingsTable
                    dealCode={item.deal_code}
                    trancheId={item.id}
                    key={reloadKey}
                  />
                </Box>
              );
            }}
            scroll={{
              x:
                tranchePricesTableColumns.reduce(
                  (acc, column) => acc + column.width,
                  0,
                ) + 240,
            }}
            columns={[
              ...(isAllowed
                ? [
                    {
                      title: "Actions",
                      dataIndex: "",
                      key: "",
                      width: 240,
                      render: (item) => (
                        <Box>
                          <Row alignItems="center">
                            <Button
                              icon="edit"
                              size="sm"
                              onClick={() => onRequestEdit(item)}
                            />
                            <Separator />
                            <DeleteCMBSTranchePrice
                              tranchePrice={item}
                              onDelete={onReload}
                            />
                            <Separator />
                            <Box width={130}>
                              <Button
                                text={"Manage ratings"}
                                intent="success"
                                size="sm"
                                onClick={() => setEditingTrancheId(item.id)}
                              />
                            </Box>
                          </Row>
                        </Box>
                      ),
                    },
                  ]
                : []),
                ...tranchePricesTableColumns,
            ]}
          />
        )}
      </PermissionValidator>
      <CMBSRatingsFormModal
        dealCode={dealCode}
        trancheId={editingTrancheId}
        onRequestClose={() => setEditingTrancheId(null)}
        onData={() => {
          setReloadKey(reloadKey + 1);
          setEditingTrancheId(null);
        }}
      />
    </React.Fragment>
  );
}

CMBSTranchePricesTable.propTypes = {
  data: PropTypes.array,
  pending: PropTypes.bool,
  onReload: PropTypes.func,
};

CMBSTranchePricesTable.defaultProps = {
  data: [],
  pending: false,
  onReload: () => {},
};

export default CMBSTranchePricesTable;

const tranchePricesTableColumns = [
  {
    title: "Deal code",
    key: "deal_code",
    dataIndex: "deal_code",
    width: 120,
  },
  {
    title: "Tranche class",
    key: "tranche_class",
    dataIndex: "tranche_class",
    width: 120,
  },
  {
    title: "Class amount",
    key: "class_amount",
    dataIndex: "class_amount",
    width: 150,
    render: (value) => <CurrencyFormat amount={value || 0} />,
  },
  {
    title: "Notional amount",
    key: "notional_amount",
    dataIndex: "notional_amount",
    width: 150,
    render: (value) => <CurrencyFormat amount={value || 0} />,
  },
  {
    title: "Denomination",
    key: "denomination.editorial_curr_name",
    dataIndex: "denomination.editorial_curr_name",
    width: 240,
  },
  {
    title: "Denomination amount",
    key: "denomination_amount",
    dataIndex: "denomination_amount",
    width: 240,
    render: (value) => <CurrencyFormat amount={value || 0} />,
  },
  {
    title: "Subord percent",
    key: "subord_percent",
    dataIndex: "subord_percent",
    width: 120,
    render: (value) => <PercentFormat amount={value || 0} />,
  },
  {
    title: "Coupon",
    key: "coupon",
    dataIndex: "coupon",
    width: 120,
    render: (value) => <PercentFormat amount={value || 0} />,
  },
  {
    title: "Price",
    key: "price",
    dataIndex: "price",
    width: 150,
    render: (value) => <CurrencyFormat amount={value || 0} />,
  },
  {
    title: "Yield",
    key: "yield",
    dataIndex: "yield",
    width: 120,
    render: (value) => <PercentFormat amount={value || 0} />,
  },
  {
    title: "Maturity Date",
    key: "maturity_date",
    dataIndex: "maturity_date",
    width: 150,
  },
  {
    title: "Weighted-average life",
    key: "wal",
    dataIndex: "wal",
    width: 240,
  },
  {
    title: "Weighted-average life, extended",
    key: "wal_ext",
    dataIndex: "wal_ext",
    width: 240,
  },
  {
    title: "Spread",
    key: "spread",
    dataIndex: "spread",
    width: 240,
  },
  {
    title: "Benchmark",
    key: "benchmark",
    dataIndex: "benchmark",
    width: 240,
  },
  {
    title: "Rate type",
    key: "rate",
    dataIndex: "rate",
    width: 150,
  },
  {
    title: "Class Buyer",
    key: "class_buyer.long_name",
    dataIndex: "class_buyer.long_name",
    width: 240,
  },
  {
    title: "Projected spread",
    key: "proj_spread",
    dataIndex: "proj_spread",
    width: 240,
  },
];
